import { z } from 'zod';

import { actorSchema, ageSchema, codedRefSchema, reasonSchema } from './clinical-common.ts';
import { birthInputSchema, patientBannerSchema } from './patients.ts';

// Casualty: registering an unknown patient, the unidentified list, and merging an unknown
// record into the identified patient's (docs/plans/phase2-casualty.md §3; ADR 0024).
// Descriptions and reasons are PHI: request bodies only, never a URL, never logged.

export const unknownDescriptionSchema = z.string().trim().min(1).max(500);

export const registerUnknownPatientRequestSchema = z
  .object({
    /** Generated once per form; a retried request returns the same patient and visit. */
    clientRequestId: z.uuid(),
    /** A casualty service point at the session's facility. */
    servicePointId: z.uuid(),
    /** From the tenant's sex value set; null uses its "unknown" concept (plan C2). */
    sex: codedRefSchema.nullable().default(null),
    /** An estimated age when one can be judged; never required. */
    birth: birthInputSchema.nullable().default(null),
    /** Where found, clothing, marks. Optional. */
    description: unknownDescriptionSchema.nullable().default(null),
    /** Backdated arrival earlier today, with a reason, as at check-in. */
    arrivedAt: z.iso.datetime().optional(),
    arrivalReason: reasonSchema.optional(),
  })
  .refine((v) => (v.arrivedAt === undefined) === (v.arrivalReason === undefined), {
    message: 'A backdated arrival time needs a reason',
    path: ['arrivalReason'],
  });

export type RegisterUnknownPatientRequest = z.infer<typeof registerUnknownPatientRequestSchema>;

export const registerUnknownPatientResultSchema = z.object({
  patientId: z.uuid(),
  visitId: z.uuid(),
  queueEntryId: z.uuid(),
  servicePointId: z.uuid(),
  temporaryLabel: z.string(),
});

export type RegisterUnknownPatientResult = z.infer<typeof registerUnknownPatientResultSchema>;

/** One unidentified patient still at the facility (plan §1.1 item 10). */
export const unidentifiedPatientSchema = z.object({
  patientId: z.uuid(),
  temporaryLabel: z.string(),
  patientNumber: z.string(),
  /** Short sex display, e.g. "M" or "U". */
  sex: z.string(),
  age: ageSchema.nullable(),
  ageEstimated: z.boolean(),
  /** The open emergency or inpatient visit, if any. */
  visit: z
    .object({
      id: z.uuid(),
      type: z.enum(['emergency', 'inpatient', 'outpatient', 'walk_in', 'mortuary']),
      startedAt: z.iso.datetime(),
      /** Where the patient is now: a queue or a ward, in words. */
      location: z.string().nullable(),
    })
    .nullable(),
  /** A merge request is waiting for approval. */
  mergeRequested: z.boolean(),
  registeredAt: z.iso.datetime(),
});

export type UnidentifiedPatientView = z.infer<typeof unidentifiedPatientSchema>;

export const unidentifiedPatientListSchema = z.object({
  items: z.array(unidentifiedPatientSchema),
});

// --- Merge requests ----------------------------------------------------------------------

export const PATIENT_MERGE_STATUSES = ['requested', 'approved', 'rejected', 'withdrawn'] as const;
export type PatientMergeStatus = (typeof PATIENT_MERGE_STATUSES)[number];

export const requestPatientMergeSchema = z.object({
  clientRequestId: z.uuid(),
  /** The identified patient the unknown record belongs to. */
  targetPatientId: z.uuid(),
  /** How the person was identified (e.g. "Identified by their sister, ID seen"). */
  reason: reasonSchema,
});

export const decidePatientMergeSchema = z.object({
  /** Required to reject; optional to approve. */
  note: z.string().trim().min(1).max(500).optional(),
});

export const patientMergeSummarySchema = z.object({
  id: z.uuid(),
  status: z.enum(PATIENT_MERGE_STATUSES),
  source: z.object({
    patientId: z.uuid(),
    temporaryLabel: z.string(),
    patientNumber: z.string(),
  }),
  target: z.object({
    patientId: z.uuid(),
    displayName: z.string(),
    patientNumber: z.string(),
  }),
  requestedBy: actorSchema,
  requestedAt: z.iso.datetime(),
  decidedBy: actorSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
});

export type PatientMergeSummary = z.infer<typeof patientMergeSummarySchema>;

export const patientMergeListQuerySchema = z.object({
  status: z.enum(PATIENT_MERGE_STATUSES).default('requested'),
});

export const patientMergeListSchema = z.object({ items: z.array(patientMergeSummarySchema) });

/** What the unknown record holds, so the approver sees what will appear under the target. */
export const mergeContentsSchema = z.object({
  visits: z.array(
    z.object({
      id: z.uuid(),
      visitNumber: z.string(),
      type: z.string(),
      status: z.enum(['open', 'closed', 'cancelled']),
      startedAt: z.iso.datetime(),
    }),
  ),
  allergies: z.number().int().min(0),
  diagnoses: z.number().int().min(0),
  labOrders: z.number().int().min(0),
  prescriptions: z.number().int().min(0),
});

export const patientMergeDetailSchema = patientMergeSummarySchema.extend({
  reason: z.string(),
  decisionNote: z.string().nullable(),
  sourceBanner: patientBannerSchema,
  targetBanner: patientBannerSchema,
  unknownDescription: z.string().nullable(),
  contents: mergeContentsSchema,
  /** Why approval would be refused now; empty when it can go ahead. */
  conflicts: z.array(z.enum(['open_visit', 'admission'])),
});

export type PatientMergeDetail = z.infer<typeof patientMergeDetailSchema>;
